import React, { useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Products from './components/Products';
import About from './components/About';
import Process from './components/Process';
import Contact from './components/Contact';
import Footer from './components/Footer';
import OrderModal from './components/OrderModal';

function App() {
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [cartItems, setCartItems] = useState([]);

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  // Refs
  const homeRef = useRef(null);
  const productsRef = useRef(null);
  const aboutRef = useRef(null);
  const processRef = useRef(null);
  const contactRef = useRef(null);

  // -----------------------------
  // CART FUNCTIONS
  // -----------------------------

  const addToCart = (product) => {
    if (product.outOfStock) {
      return;
    }

    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) => item.name === product.name
      );

      if (existingItem) {
        return prevItems.map((item) =>
          item.name === product.name
            ? {
                ...item,
                quantity: item.quantity + 1,
              }
            : item
        );
      }

      return [
        ...prevItems,
        {
          ...product,
          quantity: 1,
        },
      ];
    });
  };

  const removeFromCart = (productName) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.name !== productName)
    );
  };

  const updateCartQuantity = (productName, quantity) => {
    const newQuantity = Number(quantity);

    if (newQuantity < 1 || Number.isNaN(newQuantity)) {
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.name === productName
          ? {
              ...item,
              quantity: newQuantity,
            }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  // Total number of products/units in cart
  const cartCount = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  // -----------------------------
  // ORDER MODAL
  // -----------------------------

  const openOrderModal = (productName = '') => {
    setSelectedProduct(productName);
    setIsOrderModalOpen(true);
  };

  const closeOrderModal = () => {
    setIsOrderModalOpen(false);
    setSelectedProduct('');
  };

  // -----------------------------
  // SCROLLING
  // -----------------------------

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  return (
    <div className="font-sans text-gray-800">

      <Navbar
        scrollToHome={() => scrollToSection(homeRef)}
        scrollToProducts={() => scrollToSection(productsRef)}
        scrollToAbout={() => scrollToSection(aboutRef)}
        scrollToProcess={() => scrollToSection(processRef)}
        scrollToContact={() => scrollToSection(contactRef)}
        openOrderModal={openOrderModal}
        cartCount={cartCount}
      />

      <div ref={homeRef}>
        <Hero openOrderModal={openOrderModal} />
      </div>

      <div ref={productsRef}>
        <Products addToCart={addToCart} />
      </div>

      <div ref={aboutRef}>
        <About />
      </div>

      <div ref={processRef}>
        <Process />
      </div>

      <div ref={contactRef}>
        <Contact />
      </div>

      <Footer />

      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={closeOrderModal}
        selectedProduct={selectedProduct}
        cartItems={cartItems}
        updateCartQuantity={updateCartQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCart}
      />

    </div>
  );
}

export default App;